import styled, { keyframes } from 'styled-components'

// Shimmer animation for skeleton loading
const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`

// Main card container with horizontal layout
export const CardContainer = styled.article<{ $isHovered?: boolean }>`
  display: flex;
  flex-direction: row;
  border-radius: 10px;
  border: 1px solid ${({ $isHovered }) => ($isHovered ? '#00ade6' : '#e6e6e6')};
  background-color: #ffffff;
  cursor: pointer;
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  min-height: 160px;

  &:hover {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: #00ade6;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    height: auto;
  }

  @media (hover: none) {
    &:hover {
      transform: none;
    }
  }
`

// Left image section (40% on desktop)
export const ImageSection = styled.div`
  position: relative;
  width: 40%;
  min-width: 40%;
  overflow: hidden;

  @media (max-width: 767px) {
    width: 100%;
    height: 200px;
  }
`

// Gradient overlay on image
export const ImageGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.6) 100%);
  pointer-events: none;
  z-index: 1;
`

// Actual image
export const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);

  ${CardContainer}:hover & {
    transform: scale(1.05);
  }
`

// Loading skeleton
export const ImageSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    #f0f0f0 0%,
    #e0e0e0 20%,
    #f0f0f0 40%,
    #f0f0f0 100%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite linear;
`

// Right content section (60% on desktop)
export const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  gap: 6px;
  flex: 1;
  min-width: 0;

  @media (max-width: 767px) {
    padding: 12px;
  }
`

// Content header with icon and address
export const ContentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 4px;
`

// Icon wrapper
export const IconWrapper = styled.div`
  flex-shrink: 0;
`

// Header with address and title
export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
  flex: 1;
  min-width: 0;
`

// Address with location icon
export const Address = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #666666;
  line-height: 1.3;

  svg {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
  }
`

// Title
export const Title = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

// Badge container (property type + year)
export const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

// Property type badge (colored pill)
export const PropertyTypeBadge = styled.div<{
  $type?: 'house' | 'apartment' | null
}>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  background-color: ${({ $type }) => {
    if ($type === 'house') return '#10B981'
    if ($type === 'apartment') return '#3B82F6'
    return '#6B7280'
  }};
  color: #ffffff;

  svg {
    width: 10px;
    height: 10px;
  }
`

// Year badge (neutral)
export const YearBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  background-color: #f3f4f6;
  color: #6b7280;
`

// Metrics row (3 columns)
export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 4px;

  @media (max-width: 767px) {
    gap: 6px;
  }
`

// Individual metric item
export const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

// Metric value with icon
export const MetricValue = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.2;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    opacity: 0.6;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

// Metric label
export const MetricLabel = styled.span`
  font-size: 11px;
  color: #808080;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 400;
`

// Footer with timestamp and CTA
export const FooterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
  border-top: 1px solid #f5f5f5;
  margin-top: auto;
`

// Timestamp
export const Timestamp = styled.div`
  font-size: 11px;
  color: #808080;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 11px;
    height: 11px;
    opacity: 0.6;
  }
`

// View button
export const ViewButton = styled.button`
  background: none;
  border: none;
  color: #00ade6;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: opacity 200ms ease-out;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`
